// The "?" micro-panel: how TorahTrail works, in three steps. Opened from the
// topbar help button only - never auto-shown (the UI should teach itself; this
// is the safety net, not a gate). Rides the shared modal scaffold (modal.ts):
// focus-trapped, Escape/scrim/Close all close, focus returns to the opener.

import { el } from "../lib/dom.ts";
import { openModal } from "./modal.ts";

const TITLE = "How TorahTrail works";

const STEPS: Array<[string, string]> = [
  ["Open a source.", 'Tap any pasuk to reveal its sources in the "Explore next" panel, then tap "Open" on a commentary to read its full text right there.'],
  ["Follow a source from there.", 'Every source you open reveals the sources inside it - tap "Open" on any of them to keep following the trail as deep as you like.'],
  ["Your trail is saved as you go.", 'Never lose your place - jump back with Back, Return to start, or reopen "Your trail" whenever you like.'],
];

// ONLY the keep-actions that actually render as buttons on a source card
// (sourceCard.ts: Add to Desk + Bookmark). There is NO "Pin" / review-list UI
// (the dead SM-2 review scaffold was removed 2026-06-22). NEVER list a verb
// without a real button behind it; help text must match the rendered UI.
const VERBS: Array<[string, string]> = [
  ["Add to Desk", "keeps a source open beside the text so you can compare them side by side."],
  ["Bookmark", "saves a source to return to in a later session."],
];

// When a source is open, its links split into three honest groups. Naming them
// here teaches the cited / commentary / related distinction once, so the cards
// stay uncluttered. Wording mirrors the in-app bucket headings exactly.
const BUCKETS: Array<[string, string]> = [
  ["What this text cites", "the earlier sources this text draws on."],
  ["Commentaries on this", "later teachers who explain this source."],
  ["Where else this comes up", "other passages connected to it - each tagged by kind (Talmud, midrash, parallel verses). Open any to see the connection."],
];

export function openHelp(): void {
  openModal(
    TITLE,
    () => {
      const body = el("div", "help-body");
      body.append(
        el(
          "p",
          "help-intro",
          "TorahTrail turns a single verse into a journey. Start with the Chumash, open any commentary, and follow each source to the sources it cites - as far as your curiosity takes you.",
        ),
      );
      const list = el("ol", "help-steps");
      for (const [lead, rest] of STEPS) {
        const li = el("li", "help-step");
        li.append(el("strong", "", lead), document.createTextNode(` ${rest}`));
        list.append(li);
      }
      body.append(list);
      // The three source-link groups (cited / commentary / related), named once.
      body.append(el("h3", "help-legend-title", "Open a source, and its connections split three ways:"));
      const buckets = el("div", "help-verbs");
      for (const [name, what] of BUCKETS) {
        const line = el("p", "help-verb");
        line.append(el("strong", "", name), document.createTextNode(` - ${what}`));
        buckets.append(line);
      }
      body.append(buckets);
      body.append(el("h3", "help-legend-title", "Keep the sources that matter:"));
      const verbs = el("div", "help-verbs");
      for (const [verb, what] of VERBS) {
        const line = el("p", "help-verb");
        line.append(el("strong", "", verb), document.createTextNode(` ${what}`));
        verbs.append(line);
      }
      body.append(verbs);
      // Quiet attribution footer.
      const credit = el("p", "help-credit");
      const link = el("a", "foot-link", "powerjews.com");
      link.href = "https://powerjews.com";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      credit.append(document.createTextNode("Powered by "), link, document.createTextNode(" - b'siyata dishmaya"));
      body.append(credit);
      return body;
    },
    { modalClass: "help-panel" },
  );
}
